"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleX as XCircle, UserCheck, DollarSign, Zap, ShieldCheck, Target, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const comparisonPoints = [{
  id: 1,
  title: "Direct Access & Senior Expertise",
  icon: UserCheck,
  blushHighlight: "You work 100% directly with me—Yassine Essaadi.",
  consultantText: "No account managers, intern hand-offs, or broken games of telephone. You receive direct senior-level SEO execution from day one.",
  agencyText: "Pitched by senior directors, but your account is quickly delegated to junior execs or offshore interns."
}, {
  id: 2,
  title: "Cost Efficiency & Zero Overhead Markup",
  icon: DollarSign,
  blushHighlight: "100% of your budget goes into actual SEO work.",
  consultantText: "Without shiny agency office overheads, account manager salaries, or corporate margins, your budget delivers maximum strategic value.",
  agencyText: "High monthly retainers inflated to cover agency account managers, lavish offices, and corporate profit margins."
}, {
  id: 3,
  title: "Bespoke & Agile SEO Strategy",
  icon: Target,
  blushHighlight: "Strategies built uniquely around your revenue goals.",
  consultantText: "I build bespoke technical, content, and AI search roadmaps engineered specifically for your domain and business model.",
  agencyText: "Rigid cookie-cutter packages, automated audit templates, and standardized deliverables that ignore real nuances."
}, {
  id: 4,
  title: "Rapid Execution & Zero Bureaucracy",
  icon: Zap,
  blushHighlight: "Agile optimizations deployed without weeks of waiting.",
  consultantText: "I communicate directly with your dev team or implement changes directly, dramatically cutting turnaround times.",
  agencyText: "Endless internal review loops, slow sign-offs, and multi-tier approval chains that stall progress."
}, {
  id: 5,
  title: "Honest Measurement & Clear Deliverables",
  icon: ShieldCheck,
  blushHighlight: "Plain-English reporting focused on bottom-line business impact.",
  consultantText: "Transparent reporting on search visibility, qualified organic traffic, and leads—not vanity metrics designed to hide lack of growth.",
  agencyText: "Automated monthly PDF reports full of jargon and vanity graphs that obscure real ROI."
}, {
  id: 6,
  title: "Modern AI & Technical Search Integration",
  icon: Sparkles,
  blushHighlight: "Cutting-edge AI search optimization tailored for Google's latest updates.",
  consultantText: "I continually refine strategies to capture visibility in modern AI overview features, technical Search Generative Experiences, and traditional search.",
  agencyText: "Outdated SEO tactics reliant on outdated link schemes that fail to adapt to rapid AI updates."
}];
export default function Consultantvsagency() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-accent/20">
            Why Work Directly With Me
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            Freelance SEO Consultant vs Traditional Agency
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
            Discover why ambitious UK and international businesses choose direct collaboration with an independent SEO expert over bloated agency retainers.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {comparisonPoints.map((point, i) => {
          const IconComponent = point.icon;
          return <motion.div key={point.id} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-sm flex flex-col justify-between">
                  <div>
                    <CardHeader className="space-y-3 pb-3">
                      <div className="flex items-center justify-between">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                          <IconComponent className="size-6" />
                        </div>
                        <span className="text-xs font-semibold text-muted-foreground font-mono">
                          0{point.id}
                        </span>
                      </div>
                      <CardTitle className="text-xl font-bold text-foreground font-serif">
                        {point.title}
                      </CardTitle>
                      <div className="p-2.5 rounded-md bg-secondary/80 border-l-2 border-accent text-xs font-medium text-foreground">
                        {point.blushHighlight}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 text-sm leading-relaxed font-sans pt-2">
                      <div className="space-y-1.5 bg-background/60 p-3 rounded-lg border border-border/50">
                        <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wide">
                          <CheckCircle2 className="size-4 text-accent shrink-0" />
                          <span>Working With Me</span>
                        </div>
                        <p className="text-foreground/90 font-medium">
                          {point.consultantText}
                        </p>
                      </div>

                      <div className="space-y-1.5 p-3 rounded-lg bg-muted/40 border border-border/30">
                        <div className="flex items-center gap-2 text-muted-foreground font-medium text-xs uppercase tracking-wide">
                          <XCircle className="size-4 text-muted-foreground/70 shrink-0" />
                          <span>Typical Agency Model</span>
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {point.agencyText}
                        </p>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="rounded-2xl bg-muted/50 border border-border p-8 sm:p-10 lg:p-12 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-8 space-y-4">
              <Badge variant="outline" className="border-accent text-foreground bg-background/80">
                Direct Partnership Guarantee
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                Ready to cut out the agency middleman and scale your organic search growth?
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed">
                I limit my active client roster so every campaign receives my full personal attention, deep technical rigor, and bespoke strategic oversight. Let&apos;s discuss your search goals directly.
              </p>
            </div>
            
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4 justify-center items-stretch lg:items-end">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium w-full sm:w-auto">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent/10 hover:text-foreground w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}